import { Link, useNavigate, getRouteApi } from "@tanstack/react-router";
import { AvisosTitulo } from "@/components/AvisosTitulo";
import { BotoesJanela } from "@/components/BotoesJanela";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";
import { dataBR, hojeISO, num } from "@/lib/estoque";
import { DataBR } from "@/components/DataBR";
import { Permissao } from "@/hooks/useNivel";
import { Button } from "@/components/ui/button";
import { PrintPortal } from "@/components/PrintPortal";
import { consumirRolagem, gravarRolagem } from "@/lib/rolagem";
import { obterClienteDados } from "@/lib/obter-dados";
import { useJanela } from "@/components/JanelaContexto";

const Route = getRouteApi("/_authenticated/insumos-consumo");

const CHAVE_ROLAGEM = "rolagem:insumos-consumo";

const SEMANA = [
  "domingo",
  "segunda-feira",
  "terça-feira",
  "quarta-feira",
  "quinta-feira",
  "sexta-feira",
  "sábado",
];

/** Retorna a data de ontem no formato ISO (YYYY-MM-DD). */
export function ontemISO() {
  const hoje = new Date(`${hojeISO()}T12:00:00`);
  hoje.setDate(hoje.getDate() - 1);
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}-${String(hoje.getDate()).padStart(2, "0")}`;
}

/** Descreve a data com o dia da semana e os prefixos Hoje/Ontem. */
function rotuloDia(iso: string) {
  const d = new Date(`${iso}T12:00:00`);
  const nome = SEMANA[d.getDay()] ?? "";
  const hoje = hojeISO();
  const ontem = new Date(`${hoje}T12:00:00`);
  ontem.setDate(ontem.getDate() - 1);
  const ontemStr = `${ontem.getFullYear()}-${String(ontem.getMonth() + 1).padStart(2, "0")}-${String(ontem.getDate()).padStart(2, "0")}`;
  if (iso === hoje) return `Hoje, ${nome}`;
  if (iso === ontemStr) return `Ontem, ${nome}`;
  return nome.charAt(0).toUpperCase() + nome.slice(1);
}

/** Abertura pela rota: usa a data da URL e volta para a aba de vendas. */
export function ConsumoMaterialPage() {
  const navigate = useNavigate();
  const busca = Route.useSearch();
  const [data, setData] = useState(busca.data);
  return (
    <ConteudoConsumoMaterial
      data={data}
      onData={setData}
      onVoltar={() => void navigate({ to: "/vendas" })}
    />
  );
}

/** Versão autossuficiente para abrir em janela (MenuMetro): gerencia a própria data. */
export function ConsumoMaterialJanela() {
  const fechar = useJanela();
  const [data, setData] = useState(hojeISO());
  return <ConteudoConsumoMaterial data={data} onData={setData} onVoltar={() => fechar?.()} />;
}

type LinhaInsumo = {
  id: string;
  nome: string;
  unidade: string;
  porUnidade: number;
  total: number;
  custo: number;
};

type Produto = { id: string; nome: string; qtd: number; itens: LinhaInsumo[] };

export function ConteudoConsumoMaterial({
  data,
  onData,
  onVoltar,
}: {
  data: string;
  onData: (novo: string) => void;
  onVoltar: () => void;
}) {
  const [imprimindo, setImprimindo] = useState<"tudo" | "sumario" | null>(null);

  const { data: consumo, isLoading } = useQuery({
    queryKey: ["consumo-material", data],
    queryFn: async () => {
      const banco = await obterClienteDados();
      const { data: vendas, error } = await (banco as any)
        .from("sales")
        .select("id, sale_items(quantidade, flavor_id, flavors(id, nome))")
        .eq("data", data);
      if (error) throw error;

      const porProduto = new Map<string, { nome: string; qtd: number }>();
      for (const v of vendas ?? []) {
        for (const it of v.sale_items ?? []) {
          const id = it.flavor_id;
          const atual = porProduto.get(id) ?? {
            nome: it.flavors?.nome ?? "(produto removido)",
            qtd: 0,
          };
          atual.qtd += Number(it.quantidade ?? 0);
          porProduto.set(id, atual);
        }
      }
      if (!porProduto.size) return { produtos: [] as Produto[], consolidado: [] as LinhaInsumo[] };

      const { data: receitas, error: erroRec } = await (banco as any)
        .from("flavor_ingredients")
        .select("flavor_id, quantidade, insumos(id, nome, unidadeTrabalho, custo_unitario)")
        .in("flavor_id", [...porProduto.keys()]);
      if (erroRec) throw erroRec;

      const consolidado = new Map<string, LinhaInsumo>();
      const produtos: Produto[] = [...porProduto.entries()].map(([id, p]) => {
        const itens: LinhaInsumo[] = (receitas ?? [])
          .filter((r) => r.flavor_id === id && r.insumos)
          .map((r) => {
            const ins = r.insumos!;
            const porUnidade = Number(r.quantidade ?? 0);
            const total = porUnidade * p.qtd;
            const custo = total * Number(ins.custo_unitario ?? 0);
            const acc = consolidado.get(ins.id) ?? {
              id: ins.id,
              nome: ins.nome,
              unidade: ins.unidadeTrabalho ?? "",
              porUnidade: 0,
              total: 0,
              custo: 0,
            };
            acc.total += total;
            acc.custo += custo;
            consolidado.set(ins.id, acc);
            return {
              id: ins.id,
              nome: ins.nome,
              unidade: ins.unidadeTrabalho ?? "",
              porUnidade,
              total,
              custo,
            };
          })
          .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
        return { id, nome: p.nome, qtd: p.qtd, itens };
      });

      produtos.sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
      return {
        produtos,
        consolidado: [...consolidado.values()].sort((a, b) =>
          a.nome.localeCompare(b.nome, "pt-BR"),
        ),
      };
    },
  });

  const consolidado = useMemo(() => consumo?.consolidado ?? [], [consumo?.consolidado]);
  const produtos = useMemo(() => consumo?.produtos ?? [], [consumo?.produtos]);

  const custoTotal = useMemo(() => consolidado.reduce((s, i) => s + i.custo, 0), [consolidado]);

  // Restaura a rolagem ao voltar da edição de produto.
  useEffect(() => {
    if (!isLoading && consumo) consumirRolagem(CHAVE_ROLAGEM);
  }, [isLoading, consumo]);

  const tituloRel = `Consumo de material no dia ${dataBR(data)}`;

  function semDados() {
    if (consolidado.length) return false;
    toast.error("Não há consumo registrado nesta data.");
    return true;
  }

  /** Monta o PDF do relatório, com ou sem o detalhamento por produto. */
  function montarPDF(completo: boolean) {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    doc.setFontSize(14);
    doc.text(completo ? tituloRel : `${tituloRel} — sumário`, 40, 40);
    autoTable(doc, {
      startY: 60,
      head: [["Consolidado do dia", "Consumo total", "Custo R$"]],
      body: consolidado.map((i) => [
        i.nome,
        `${num(i.total, 3)} ${i.unidade}`.trim(),
        num(i.custo, 2),
      ]),
      foot: [["Custo total do material consumido", "", num(custoTotal, 2)]],
      styles: { fontSize: 10 },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
    });

    if (completo) {
      for (const p of produtos) {
        autoTable(doc, {
          head: [
            [`${p.nome} — ${num(p.qtd, 0)} vendido(s)`, "Por unidade", "Consumo total", "Custo R$"],
          ],
          body: p.itens.length
            ? p.itens.map((i) => [
                i.nome,
                `${num(i.porUnidade, 3)} ${i.unidade}`.trim(),
                `${num(i.total, 3)} ${i.unidade}`.trim(),
                num(i.custo, 2),
              ])
            : [["Sem insumos cadastrados na receita.", "", "", ""]],
          styles: { fontSize: 9 },
          columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
        });
      }
    }
    return doc;
  }

  function exportar(completo: boolean) {
    if (semDados()) return;
    montarPDF(completo).save(`consumo-material-${completo ? "completo" : "sumario"}-${data}.pdf`);
  }

  function imprimir(tipo: "tudo" | "sumario") {
    if (semDados()) return;
    setImprimindo(tipo);
    setTimeout(() => {
      window.print();
      setImprimindo(null);
    }, 80);
  }

  const celula = { padding: 4, borderBottom: "1px solid #ccc" } as const;

  return (
    <Permissao nivelMaximo={3}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex items-center gap-2">
            <BotoesJanela />
            <h1 className="text-2xl font-semibold">Consumo de material</h1>
            <AvisosTitulo />
          </div>
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap text-sm font-medium text-muted-foreground">
              {rotuloDia(data)}
            </span>
            <DataBR id="data-consumo" value={data} onChange={onData} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => imprimir("tudo")}>
            Imprimir tudo
          </Button>
          <Button variant="outline" onClick={() => imprimir("sumario")}>
            Imprimir sumário
          </Button>
          <Button variant="outline" onClick={() => exportar(true)}>
            Exportar tudo
          </Button>
          <Button variant="outline" onClick={() => exportar(false)}>
            Exportar sumário
          </Button>
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}

        {!isLoading && !produtos.length && (
          <p className="text-sm text-muted-foreground">
            Não há vendas registradas em {dataBR(data)}.
          </p>
        )}

        {!isLoading && !!consolidado.length && (
          <section className="space-y-2">
            <h2 className="text-lg font-semibold">Consolidado do dia</h2>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-2 text-left">Insumo</th>
                    <th className="p-2 text-right">Consumo total</th>
                    <th className="p-2 text-right">Custo R$</th>
                  </tr>
                </thead>
                <tbody>
                  {consolidado.map((i) => (
                    <tr key={i.id} className="border-t border-border/60">
                      <td className="p-2">{i.nome}</td>
                      <td className="p-2 text-right font-medium">
                        {num(i.total, 3)} {i.unidade}
                      </td>
                      <td className="p-2 text-right">{num(i.custo, 2)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-border bg-muted/40 font-semibold">
                    <td className="p-2" colSpan={2}>
                      Custo total do material consumido
                    </td>
                    <td className="p-2 text-right">{num(custoTotal, 2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

        {!isLoading &&
          produtos.map((p) => (
            <section key={p.id} className="space-y-2">
              <h2 className="text-lg font-semibold">
                <Link
                  to="/produtos"
                  search={{ editar: p.id, voltarConsumo: data }}
                  onClick={() => gravarRolagem(CHAVE_ROLAGEM)}
                  className="text-primary hover:underline"
                >
                  {p.nome}
                </Link>{" "}
                <span className="text-sm font-normal text-muted-foreground">
                  — {num(p.qtd, 0)} vendido(s)
                </span>
              </h2>
              {p.itens.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Este produto não possui insumos cadastrados na receita.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-2 text-left">Insumo</th>
                        <th className="p-2 text-right">Por unidade</th>
                        <th className="p-2 text-right">Consumo total</th>
                        <th className="p-2 text-right">Custo R$</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.itens.map((i) => (
                        <tr key={i.id} className="border-t border-border/60">
                          <td className="p-2">{i.nome}</td>
                          <td className="p-2 text-right">
                            {num(i.porUnidade, 3)} {i.unidade}
                          </td>
                          <td className="p-2 text-right font-medium">
                            {num(i.total, 3)} {i.unidade}
                          </td>
                          <td className="p-2 text-right">{num(i.custo, 2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}

        {imprimindo && (
          <PrintPortal>
            <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{tituloRel}</h1>
            <h2 style={{ fontSize: 14, fontWeight: 600, margin: "10px 0 4px" }}>
              Consolidado do dia
            </h2>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", borderBottom: "1px solid #000", padding: 4 }}>
                    Insumo
                  </th>
                  <th style={{ textAlign: "right", borderBottom: "1px solid #000", padding: 4 }}>
                    Consumo total
                  </th>
                  <th style={{ textAlign: "right", borderBottom: "1px solid #000", padding: 4 }}>
                    Custo R$
                  </th>
                </tr>
              </thead>
              <tbody>
                {consolidado.map((i) => (
                  <tr key={i.id}>
                    <td style={celula}>{i.nome}</td>
                    <td style={{ ...celula, textAlign: "right" }}>
                      {num(i.total, 3)} {i.unidade}
                    </td>
                    <td style={{ ...celula, textAlign: "right" }}>{num(i.custo, 2)}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ padding: 4, fontWeight: 600 }} colSpan={2}>
                    Custo total do material consumido
                  </td>
                  <td style={{ padding: 4, textAlign: "right", fontWeight: 600 }}>
                    {num(custoTotal, 2)}
                  </td>
                </tr>
              </tbody>
            </table>

            {imprimindo === "tudo" &&
              produtos.map((p) => (
                <div key={p.id} style={{ marginTop: 14 }}>
                  <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                    {p.nome} — {num(p.qtd, 0)} vendido(s)
                  </h2>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th
                          style={{ textAlign: "left", borderBottom: "1px solid #000", padding: 4 }}
                        >
                          Insumo
                        </th>
                        <th
                          style={{ textAlign: "right", borderBottom: "1px solid #000", padding: 4 }}
                        >
                          Por unidade
                        </th>
                        <th
                          style={{ textAlign: "right", borderBottom: "1px solid #000", padding: 4 }}
                        >
                          Consumo total
                        </th>
                        <th
                          style={{ textAlign: "right", borderBottom: "1px solid #000", padding: 4 }}
                        >
                          Custo R$
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.itens.map((i) => (
                        <tr key={i.id}>
                          <td style={celula}>{i.nome}</td>
                          <td style={{ ...celula, textAlign: "right" }}>
                            {num(i.porUnidade, 3)} {i.unidade}
                          </td>
                          <td style={{ ...celula, textAlign: "right" }}>
                            {num(i.total, 3)} {i.unidade}
                          </td>
                          <td style={{ ...celula, textAlign: "right" }}>{num(i.custo, 2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
          </PrintPortal>
        )}
      </div>
    </Permissao>
  );
}

/** Nome deste arquivo, injetado em build (não editar). */
export const ARQUIVO = "insumos-consumo.tsx";
